import { Fragment, useState, type ReactNode } from "react";
import { admin, type Run } from "../../api/admin";

// A URL in a log line is the thing you want to open: the page that was
// challenged, the release that would not match. http(s) runs are turned
// into links that open in a new tab; a trailing bracket or full stop
// belongs to the sentence, not the address.
const reURL = /https?:\/\/[^\s<>"']+/g;

export function Linkified({ text }: { text: string }) {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(reURL)) {
    let url = m[0];
    let trail = "";
    while (/[)\].,;:!?]$/.test(url) && !(url.endsWith(")") && url.includes("("))) {
      trail = url.slice(-1) + trail;
      url = url.slice(0, -1);
    }
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(
      <a key={at} href={url} target="_blank" rel="noopener noreferrer">{url}</a>,
    );
    if (trail) out.push(trail);
    last = at + m[0].length;
  }
  if (out.length === 0) return <>{text}</>;
  if (last < text.length) out.push(text.slice(last));
  return <>{out.map((n, i) => <Fragment key={i}>{n}</Fragment>)}</>;
}

export function StateBadge({ state }: { state: Run["state"] }) {
  return <span className={`state ${state}`}>{state}</span>;
}

export function Progress({ run }: { run: Run }) {
  const total = run.progressTotal ?? 0;
  const done = run.progressDone ?? 0;
  if (total <= 0) return null;
  const pct = Math.min(100, Math.round((done / total) * 100));
  return (
    <div className="progress" title={`${done} of ${total}`}>
      <div className="bar" style={{ width: `${pct}%` }} />
      <span className="pct">
        {done.toLocaleString()} / {total.toLocaleString()}
        {run.progressNote && <em> · {run.progressNote}</em>}
      </span>
    </div>
  );
}

export function Counters({ counters }: { counters: Record<string, number> }) {
  const keys = Object.keys(counters ?? {}).sort();
  if (keys.length === 0) return null;
  return (
    <span className="counters">
      {keys.map((k) => (
        <span key={k} className="counter">
          <b>{counters[k]!.toLocaleString()}</b> {k}
        </span>
      ))}
    </span>
  );
}

export function when(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  // Safari and Firefox parse only ISO 8601; anything else is Invalid Date,
  // which is worth showing as the text rather than as "Invalid Date".
  if (Number.isNaN(d.getTime())) return iso;
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs > -60 && secs < 0) return "in a moment";
  if (secs < 0) {
    const ahead = -secs;
    if (ahead < 3600) return `in ${Math.round(ahead / 60)}m`;
    if (ahead < 86400) return `in ${Math.round(ahead / 3600)}h`;
  }
  return d.toLocaleString();
}

export function took(run: Run): string {
  if (!run.startedAt) return "";
  const end = run.finishedAt ? new Date(run.finishedAt) : new Date();
  const secs = Math.round((end.getTime() - new Date(run.startedAt).getTime()) / 1000);
  if (Number.isNaN(secs)) return "";
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
}

/**
 * The one button for ending a run, wherever a run is shown. A running run is
 * asked to stop and does so at its next checkpoint; a queued one is taken out
 * of the queue there and then -- nothing is running, so there is nothing to
 * wait for, and the kind is free again for a corrected run. Anything already
 * finished gets no button.
 */
export function StopRun({ run, onDone }: { run: Run; onDone?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (run.state !== "running" && run.state !== "queued") return null;
  const queued = run.state === "queued";
  const pending = run.cancelRequested && !queued;
  return (
    <>
      <button
        className="danger"
        disabled={busy || pending}
        title={queued ? "take it out of the queue; it never starts" : "stop at the next checkpoint, keeping what is done"}
        onClick={(e) => {
          e.stopPropagation();
          setBusy(true); setErr(null);
          admin.cancel(run.id)
            .then(() => onDone?.())
            .catch((x: Error) => setErr(x.message))
            .finally(() => setBusy(false));
        }}
      >
        {pending ? "stopping…" : queued ? "Remove from queue" : "Stop"}
      </button>
      {err && <span className="failmsg">{err}</span>}
    </>
  );
}
